import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {IUser} from '../../../../shared/interfaces/user.interface';
import {Select, Store} from '@ngxs/store';
import {AuthState} from '../../../../shared/states/auth/auth.state';
import {Observable} from 'rxjs/internal/Observable';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {StrongPasswordValidator} from '../../../../shared/validators/strong-password.validator';
import {CustomEmailValidator} from '../../../../shared/validators/custom-email.validator';
import {CheckPinBelongsToUser, UpdateAccountPIN, UpdateUser} from '../../../../shared/states/profile/profile.actions';
import {take, withLatestFrom} from 'rxjs/operators';
import {ProfileState} from '../../../../shared/states/profile/profile.state';
import {Logout, PersistUserToLocalStorage} from '../../../../shared/states/auth/auth.actions';
import {SamePasswordValidator} from '../../../../shared/validators/same-password.validator';
import {InvalidPasswordValidator} from '../../../../shared/validators/invalid-password.validator';

@UntilDestroy()
@Component({
  selector: 'app-edit-profile-dialog',
  templateUrl: './edit-profile-dialog.component.html',
  styleUrls: ['./edit-profile-dialog.component.scss'],
})
export class EditProfileDialogComponent implements OnInit {
  @Input() show: boolean;

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() afterSaved: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('lastNameInput') lastNameInput;
  @ViewChild('emailInput') emailInput;
  @ViewChild('currentPasswordInput') currentPasswordInput;
  @ViewChild('newPasswordInput') newPasswordInput;
  @ViewChild('currentPinInput') currentPinInput;
  @ViewChild('newPinInput') newPinInput;
  @ViewChild('saveChangesButton') saveChangesButton;

  editAccountForm: UntypedFormGroup;

  showUpdatePassword: boolean;
  showUpdatePIN: boolean;
  currentUser: IUser;

  isInvalidCurrentPin: boolean;
  isCurrentPasswordInvalid: boolean;

  @Select(AuthState.currentUser) currentUser$: Observable<IUser>;
  @Select(ProfileState.success) success$: Observable<boolean>;
  @Select(ProfileState.editUserResponse) response$: Observable<any>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
  ) {
    this.editAccountForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, CustomEmailValidator]],
      currentPassword: ['', [
        Validators.required,
        InvalidPasswordValidator(this.isCurrentPasswordInvalid),
        SamePasswordValidator('newPassword'),
      ]],
      newPassword: ['', [
        Validators.required,
        StrongPasswordValidator,
        SamePasswordValidator('currentPassword', true),
      ]],
      currentPIN: ['', [Validators.required]],
      newPIN: ['', [Validators.required]],
    });
  }

  pinErrorMessage: string;

  ngOnInit(): void {
    this.currentUser$.pipe(untilDestroyed(this))
      .subscribe((user: IUser) => {
        if (user) {
          this.currentUser = user;
          this.editAccountForm.patchValue({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          });
        }
      });

    // disable hidden fields to make form valid without them
    this.updatePasswordEnableState(false);
    this.updatePINEnableState(false);
  }

  setCurrentPIN(pin: string): void {
    this.isInvalidCurrentPin = false;

    if (pin && this.newPinInput && this.newPinInput.pinInputList) {
      setTimeout(() => this.newPinInput.pinInputList.first.nativeElement.focus());

      this.store.dispatch(new CheckPinBelongsToUser(pin))
        .pipe(take(1), withLatestFrom(this.success$))
        .subscribe(([_, success]) => {
          this.editAccountForm.get('currentPIN').patchValue(success ? pin : '');

          if (!success) {
            this.isInvalidCurrentPin = true;
            this.currentPinInput.clearInputs();
          }
        });
    }
  }

  setNewPIN(pin: string): void {
    this.pinErrorMessage = '';
    this.editAccountForm.get('newPIN').patchValue(pin);
    setTimeout(() => this.saveChangesButton.focus());
  }

  toggleUpdatePassword(show: boolean): void {
    this.showUpdatePassword = show;
    this.focusInput('currentPasswordInput');
    this.updatePasswordEnableState(show);

    if (!show) {
      this.editAccountForm.get('currentPassword').reset();
      this.editAccountForm.get('newPassword').reset();
    }
  }

  toggleUpdatePIN(show: boolean): void {
    this.showUpdatePIN = show;
    this.updatePINEnableState(show);

    if (show && this.currentPinInput && this.currentPinInput.pinInputList) {
      setTimeout(() => this.currentPinInput.pinInputList.first.nativeElement.focus());
    }

    if (!show) {
      this.editAccountForm.get('currentPIN').reset();
      this.editAccountForm.get('newPIN').reset();
    }
  }

  updatePasswordEnableState(show): void {
    this.editAccountForm.get('currentPassword')[show ? 'enable' : 'disable']();
    this.editAccountForm.get('newPassword')[show ? 'enable' : 'disable']();
    this.editAccountForm.updateValueAndValidity();
  }

  updatePINEnableState(show): void {
    this.editAccountForm.get('currentPIN')[show ? 'enable' : 'disable']();
    this.editAccountForm.get('newPIN')[show ? 'enable' : 'disable']();
    this.editAccountForm.updateValueAndValidity();
  }

  updateUser(): void {
    const formValue = this.editAccountForm.value;
    // TODO - the below fields shouldn't be necessary
    // we ought to use a different API end point and only update the logged in user's email/first& lastnames.
    formValue.createDate = new Date();
    formValue.id = this.currentUser.id;
    formValue.status = 1;

    this.store.dispatch(new UpdateUser(formValue))
      .pipe(take(1), withLatestFrom(this.success$, this.response$))
      .subscribe(([_, success, response]) => {
        if (response) {
          if (response.message === 'Username Taken') {
            this.editAccountForm.get('email').setErrors({emailTaken: true});
          } else if (response.message === 'Logout required') {
            this.store.dispatch(new Logout());
          } else if (response.message === 'Authentication failed') {
            this.editAccountForm.get('currentPassword').setErrors({password: 'invalid'});
          }else {
            this.store.dispatch(new PersistUserToLocalStorage());
            this.afterSaved.emit(success);
          }
        }
      });
  }

  saveProfile(): void {
    if (this.editAccountForm.valid) {
      if (this.editAccountForm.value.newPIN) {
        this.store.dispatch(new UpdateAccountPIN(this.editAccountForm.value.newPIN))
          .pipe(take(1), withLatestFrom(this.success$))
          .subscribe(([_, success]) => {
            if (success) {
              this.updateUser();
            } else {
              this.pinErrorMessage = 'You must choose a different PIN.';
            }
          });
      } else {
        this.updateUser();
      }
    }
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }

  cancelEdit(): void {
    this.cancel.next();
  }
}
